import { mediaRef } from '@/services/mediaAssets';
import attendanceUtilities from '@/lib/traditions/attendance.cjs';
import { chickFilAFridayAttendance } from '@/data/traditions/chickFilAFridayAttendance';

const movieNightImage = mediaRef('adventures/movie-night/reckon-crew-alamo-drafthouse-cinematic-illustrated.png');
const reckonBallCoverImage = mediaRef('adventures/reckon-ball-cover.png');
const anniversaryDinnerCoverImage = mediaRef('adventures/anniversary-dinner-cover.png');
const chickFilAFridayCoverImage = mediaRef('adventures/chick-fil-a-friday-cover.png');
const happyHoursCoverImage = mediaRef('adventures/happy-hours-cover.png');
const endOfYearLunchCoverImage = mediaRef('adventures/end-of-year-lunch-cover.png');

export const chickFilAFriday = {
  id: 'chick-fil-a-friday',
  slug: 'chick-fil-a-friday',
  contentType: 'tradition',
  name: 'Chick-fil-A Friday',
  shortName: 'Chick-fil-A Friday',
  eyebrow: 'Weekly Tradition',
  heroImage: chickFilAFridayCoverImage,
  summary: "A weekly walk, a shared lunch, and one of Reckon's longest-running office traditions.",
  description: 'It started with a simple Friday lunch run. When the crew is working from the office, we walk together to Chick-fil-A, order lunch, and carry it back to our desks. Over time, the walk became as much a part of the tradition as the meal itself: a chance to step away from the screen, catch up, and close out the week together. Since September 2024, Chick-fil-A Friday has become one of the most consistent rituals in the Reckon office.',
  cadence: 'weekly',
  status: 'active',
  startDate: '2024-09-20',
  occurrenceCount: 87,
  nextMilestone: 100,
  defaultPlace: { name: 'Chick-fil-A' },
  milestones: [
    { count: 1, label: 'First Chick-fil-A Friday', date: '2024-09-20' },
    { count: 25, label: '25 Fridays Together' },
    { count: 50, label: '50 Fridays Together' },
    { count: 75, label: '75 Fridays Together' },
    { count: 87, label: '87 Confirmed Fridays', date: '2026-08-07' },
    { count: 100, label: '100 Fridays Together', description: 'Next milestone' },
  ],
  tags: ['weekly-tradition', 'office-culture', 'lunch', 'walking', 'team-connection'],
  attendance: attendanceUtilities.sortAttendanceRecords(chickFilAFridayAttendance, 10),
  featured: true,
  seoTitle: 'Chick-fil-A Friday | The Reckon Way',
  seoDescription: 'A weekly Reckon tradition that began on September 20, 2024.',
};

export const anniversaryDinner = {
  id: 'anniversary-dinner',
  slug: 'anniversary-dinner',
  contentType: 'tradition',
  name: 'Anniversary Dinner',
  eyebrow: 'Annual Tradition',
  heroImage: anniversaryDinnerCoverImage,
  summary: 'Every August, Reckon celebrates another year together with a private dinner for employees and their spouse, partner, or guest.',
  description: 'The Anniversary Dinner is designed as a thank-you to the employees building Reckon and to the people who support them throughout the year. The evening is held in a private dining room, and every guest is invited to order anything they would like from the menu.',
  cadence: 'annual',
  status: 'active',
  startDate: '2023-08-01',
  occurrenceCount: 4,
  occurrences: [
    {
      id: 'anniversary-dinner-2023', date: '2023-08-01', datePrecision: 'month', title: '2023 Anniversary Dinner', place: { name: 'DC Prime', city: 'Ashburn', state: 'VA' }, summary: 'Private dining at DC Prime.',
    },
    {
      id: 'anniversary-dinner-2024', date: '2024-08-01', datePrecision: 'month', title: '2024 Anniversary Dinner', place: { name: 'DC Prime', city: 'Ashburn', state: 'VA' }, summary: 'The Anniversary Dinner returned to DC Prime.',
    },
    {
      id: 'anniversary-dinner-2025', date: '2025-08-01', datePrecision: 'month', title: '2025 Anniversary Dinner', place: { name: "Fleming's Prime Steakhouse & Wine Bar", city: 'Tysons', state: 'VA' }, summary: "The annual dinner moved to Fleming's Prime in Tysons.",
    },
    {
      id: 'anniversary-dinner-2026', date: '2026-08-01', datePrecision: 'month', title: '2026 Anniversary Dinner', place: { name: 'DC Prime', city: 'Ashburn', state: 'VA' }, summary: 'The Anniversary Dinner returns to DC Prime.',
    },
  ],
  tags: ['annual-tradition', 'company-anniversary', 'private-dining', 'employees-and-guests', 'celebration'],
  featured: true,
};

export const happyHours = {
  id: 'happy-hours',
  slug: 'happy-hours',
  contentType: 'tradition',
  name: 'Happy Hours',
  eyebrow: 'Recurring Tradition',
  heroImage: happyHoursCoverImage,
  summary: 'A running collection of the places where the Reckon Crew has gathered after work to celebrate, connect, and unwind.',
  cadence: 'recurring',
  status: 'active',
  startDate: '2023-01-01',
  startDateLabel: 'Pre-2022',
  occurrences: [],
  locations: [
    {
      id: 'crooked-run-sterling',
      name: 'Crooked Run Fermentation',
      city: 'Sterling',
      state: 'VA',
      websiteUrl: 'https://www.crookedrunfermentation.com/',
      description: 'A spacious Sterling taproom and restaurant built around Crooked Run’s wide-ranging lineup of lagers, IPAs, sours, barrel-aged beers, natural wines, ciders, and cocktails, with plenty of food and room for a group.',
    },
    {
      id: 'ono-brewing-company',
      name: 'Ono Brewing Company',
      city: 'Chantilly',
      state: 'VA',
      websiteUrl: 'https://www.onobrewco.com/',
      description: 'A veteran- and family-owned brewery with a relaxed Hawaiian atmosphere and a self-serve wall that makes it easy to sample rotating craft beer, cider, and wine at your own pace.',
    },
    {
      id: 'bunnyman-brewing-chantilly',
      name: 'Bunnyman Brewing',
      city: 'Chantilly',
      state: 'VA',
      websiteUrl: 'https://bunnymanbrewing.com/',
      description: 'A spacious Chantilly taproom combining Bunnyman’s creative craft beer and community events with the former Mustang Sally brewery, plus sausages, fries, and other casual food from Eugene’s inside the taproom.',
    },
    {
      id: 'taco-bamba-herndon',
      name: 'Taco Bamba',
      city: 'Herndon',
      state: 'VA',
      websiteUrl: 'https://www.tacobamba.com/location/herndon-va/',
      description: 'A lively Herndon taqueria pairing a deep menu of traditional and inventive tacos with margaritas, beer, shareable snacks, and a weekday happy hour made for an easy after-work gathering.',
    },
    {
      id: 'aslin-beer-herndon',
      name: 'Aslin Beer Company',
      city: 'Herndon',
      state: 'VA',
      websiteUrl: 'https://www.aslinbeer.com/herndon',
      description: 'A busy Herndon taproom known for a constantly changing draft list that moves from hazy IPAs and lagers to fruit-forward sours, with food from IZZI and an energetic neighborhood feel.',
    },
  ],
  tags: ['happy-hour', 'after-work', 'team-connection', 'food-and-drink'],
  featured: true,
};

export const reckonBall = {
  id: 'reckon-ball',
  slug: 'reckon-ball',
  contentType: 'tradition',
  name: 'Reckon Ball',
  eyebrow: 'Annual Tradition',
  heroImage: reckonBallCoverImage,
  summary: 'An annual overnight celebration of the Reckon Crew and the guests who support them, built around memorable settings, private dining, and time together outside the office.',
  description: 'Each Reckon Ball has its own setting, personality, and stories, but the purpose remains the same: recognize the people behind our success and create memories together outside the office.',
  cadence: 'annual',
  status: 'active',
  startDate: '2022',
  occurrenceCount: 4,
  occurrences: [],
  archiveRouteName: 'reckon-ball-archive',
  tags: ['annual-tradition', 'overnight-stay', 'private-dining', 'employees-and-guests', 'celebration'],
  featured: true,
};

export const endOfYearLunch = {
  id: 'end-of-year-lunch',
  slug: 'end-of-year-lunch',
  contentType: 'tradition',
  name: 'End of Year Lunch',
  eyebrow: 'Annual Tradition',
  heroImage: endOfYearLunchCoverImage,
  summary: 'A recurring year-end gathering at Fogo de Chão in Reston, bringing the Reckon Crew together around the table before the calendar turns.',
  description: 'The End of Year Lunch gives the crew one more chance to gather, look back on the year, and enjoy an unhurried meal together. Fogo de Chão in Reston makes a natural home for the tradition with its open churrasco grill, tableside fire-roasted cuts, Market Table, and space built for group dining.',
  cadence: 'annual',
  status: 'active',
  defaultPlace: {
    name: 'Fogo de Chão',
    city: 'Reston',
    state: 'VA',
  },
  occurrences: [],
  locations: [
    {
      id: 'fogo-de-chao-reston',
      name: 'Fogo de Chão',
      city: 'Reston',
      state: 'VA',
      websiteUrl: 'https://fogodechao.com/location/reston/',
      description: 'A Brazilian steakhouse in Reston Town Center where an open-air churrasco grill, gaucho chefs, fire-roasted meats, the Market Table, and a lively bar create an easy setting for a group celebration.',
    },
  ],
  tags: ['annual-tradition', 'end-of-year', 'team-connection', 'lunch', 'celebration'],
  meritBadgeIds: [],
  featured: true,
};

export const newEmployeeLunch = {
  id: 'new-employee-lunch',
  slug: 'new-employee-lunch',
  contentType: 'tradition',
  name: 'New Employee Lunch',
  eyebrow: 'Recurring Tradition',
  summary: 'A welcome lunch for each new Reckon employee, documented through the person joining the crew and the place where everyone gathered.',
  description: 'New Employee Lunch gives the crew an opportunity to step away from work and welcome a new teammate around the table. Each entry keeps the record simple: the new employee’s name, the restaurant, the date when known, and any memory worth carrying forward.',
  cadence: 'recurring',
  status: 'active',
  occurrences: [],
  occurrenceSectionKicker: 'Welcome to the Crew',
  occurrenceSectionTitle: 'New Employee Lunches',
  emptyOccurrencesCopy: 'Add each lunch as a simple entry with the new employee’s name and the restaurant. Dates and short memories can be included whenever they are known.',
  tags: ['new-employee', 'welcome', 'team-lunch', 'team-connection'],
  meritBadgeIds: [],
  featured: true,
};

export const movieNight = {
  id: 'movie-night-alamo',
  slug: 'movie-night-alamo',
  contentType: 'tradition',
  name: 'Movie Night',
  eyebrow: 'Recurring Tradition',
  summary: 'Dinner, drinks, and a shared big-screen story—usually at Alamo Drafthouse, and always with the Reckon Crew.',
  description: 'Movie Night started with Mission: Impossible in July 2023 and grew into a recurring reason to trade the office for happy hour, dinner, and a film. Most nights have unfolded at Alamo Drafthouse One Loudoun, with employees and guests gathering before the show; the Fantastic Four outing took the tradition to the Udvar-Hazy IMAX.',
  cadence: 'recurring',
  status: 'active',
  startDate: '2023-07-18',
  occurrenceCount: 12,
  nextMilestone: 15,
  defaultPlace: {
    name: 'Alamo Drafthouse One Loudoun',
    city: 'Ashburn',
    state: 'VA',
  },
  heroImage: movieNightImage,
  occurrences: [
    {
      id: 'movie-night-mission-impossible-dead-reckoning',
      date: '2023-07-18',
      title: 'Mission: Impossible – Dead Reckoning Part One',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Ethan Hunt races across the world to keep a rogue AI from deciding humanity’s future.',
    },
    {
      id: 'movie-night-the-fall-guy',
      date: '2024-05-21',
      title: 'The Fall Guy',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A battle-scarred stuntman hunts for a missing movie star—and another shot with the woman he loves.',
    },
    {
      id: 'movie-night-despicable-me-4',
      date: '2024-07-23',
      title: 'Despicable Me 4',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Gru faces a revenge-hungry supervillain while his family adjusts to one mischievous new member.',
    },
    {
      id: 'movie-night-captain-america-brave-new-world',
      date: '2025-02-18',
      title: 'Captain America: Brave New World',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Sam Wilson steps fully into the shield while unraveling a conspiracy with global consequences.',
    },
    {
      id: 'movie-night-thunderbolts',
      date: '2025-05-06',
      title: 'Thunderbolts*',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A crew of Marvel misfits gets trapped in a deadly mission and discovers they may be stronger together.',
    },
    {
      id: 'movie-night-mission-impossible-the-final-reckoning',
      date: '2025-05-27',
      title: 'Mission: Impossible – The Final Reckoning',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Ethan Hunt makes one last impossible run to stop the Entity before it controls the world.',
    },
    {
      id: 'movie-night-jurassic-world-rebirth',
      date: '2025-07-15',
      title: 'Jurassic World Rebirth',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A covert team ventures into dinosaur territory to recover DNA that could change modern medicine.',
    },
    {
      id: 'movie-night-fantastic-four',
      date: '2025-07-31',
      title: 'Fantastic Four',
      place: { name: 'Airbus IMAX Theater at Udvar-Hazy Center', city: 'Chantilly', state: 'VA' },
      summary: 'Marvel’s first family must protect its retro-futuristic world from a planet-sized cosmic threat.',
    },
    {
      id: 'movie-night-project-hail-mary',
      date: '2026-03-19',
      title: 'Project Hail Mary',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A lone astronaut wakes light-years from home and finds Earth’s fate tied to an unlikely friendship.',
    },
    {
      id: 'movie-night-the-mandalorian-and-grogu',
      date: '2026-05-28',
      title: 'Star Wars: The Mandalorian and Grogu',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Din Djarin and Grogu take their found-family act on one more high-stakes trip across the galaxy.',
    },
    {
      id: 'movie-night-toy-story-5',
      date: '2026-06-23',
      title: 'Toy Story 5',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Woody, Buzz, and the gang face their newest rival for a child’s attention: technology.',
    },
    {
      id: 'movie-night-spider-man-brand-new-day',
      date: '2026-08-04',
      title: 'Spider-Man: Brand New Day',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'With the world forgetting Peter Parker, Spider-Man faces a new threat no one can even see.',
    },
  ],
  occurrenceSectionKicker: 'Twelve Movies and Counting',
  occurrenceSectionTitle: 'The Movie Night Archive',
  tags: ['movies', 'team-connection', 'indoor', 'local-event'],
  meritBadgeIds: [],
  featured: true,
};

export const traditions = [reckonBall, anniversaryDinner, chickFilAFriday, happyHours, endOfYearLunch, newEmployeeLunch, movieNight];
export const traditionsBySlug = traditions.reduce((lookup, tradition) => ({ ...lookup, [tradition.slug]: tradition }), {
  'end-of-year-dinner': endOfYearLunch,
});

export default traditions;
